/*
 * Copyright (c) 2025 - 2026 Ping Identity Corporation. All rights reserved.
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 */
import { createSelector } from '@reduxjs/toolkit';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query';
import { initWellknownQuery } from '@forgerock/sdk-oidc';
/**
 * RTK Query API for well-known endpoint discovery.
 *
 * Uses the `initWellknownQuery` builder pattern from `@forgerock/sdk-oidc`.
 * The builder constructs the request and validates the response;
 * `fetchBaseQuery` handles the HTTP transport through RTK Query's pipeline.
 */
export const wellknownApi = createApi({
    reducerPath: 'wellknown',
    baseQuery: fetchBaseQuery(),
    endpoints: (builder) => ({
        configuration: builder.query({
            queryFn: async (url, _api, _extra, baseQuery) => {
                const result = await initWellknownQuery(url).applyQuery(async (req) => {
                    const queryResult = await baseQuery(req);
                    return queryResult;
                });
                return result;
            },
        }),
    }),
});
/**
 * Creates a memoized selector for cached well-known data.
 *
 * @param wellknownUrl - The well-known endpoint URL used as the cache key
 * @returns A memoized selector that extracts the WellknownResponse from state, or undefined if not yet fetched
 */
export function createWellknownSelector(wellknownUrl) {
    return createSelector(wellknownApi.endpoints.configuration.select(wellknownUrl), (result) => result?.data);
}
/**
 * Convenience selector for oidc-client's RootState type.
 *
 * Unlike {@link createWellknownSelector}, this immediately evaluates the
 * selector against the provided state rather than returning a reusable selector.
 *
 * @param wellknownUrl - The well-known endpoint URL used as the cache key
 * @param state - The oidc-client Redux root state
 * @returns The cached WellknownResponse or undefined if not yet fetched
 */
export function wellknownSelector(wellknownUrl, state) {
    const selector = createSelector(wellknownApi.endpoints.configuration.select(wellknownUrl), (result) => result?.data);
    return selector(state);
}
//# sourceMappingURL=wellknown.api.js.map